import axios from 'axios';

// Base URL for the backend API
// Priority: 1. Environment variable, 2. Current origin (if relative path is preferred), 3. Localhost
const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;

    const hostname = window.location.hostname;

    // For staging/prod where frontend and backend might be on same root domain or pages.dev
    if (hostname.includes('jeevi-file-hub-staging.pages.dev')) {
        return 'https://file-hub-worker-staging.jeeviacademy.workers.dev';
    }
    if (hostname.includes('jeevi-file-hub.pages.dev')) {
        return 'https://file-hub-worker-prod.jeeviacademy.workers.dev';
    }
    if (hostname.includes('jeeviacademy.com')) {
        return window.location.origin.replace('file-hub', 'file-hub-worker');
    }
    return 'http://localhost:8787';
};

const API_BASE_URL = getApiBaseUrl();
console.log('[API] Initialized with Base URL:', API_BASE_URL);

const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor for global error logging
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('session_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    (error) => {
        console.error('[API] Global Error:', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data
        });
        return Promise.reject(error);
    }
);

// Files API
export const filesApi = {
    // List all files
    list: async (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.type) params.append('type', filters.type);
        if (filters.visibility) params.append('visibility', filters.visibility);
        if (filters.directoryId) params.append('directoryId', filters.directoryId);
        if (filters.trash) params.append('trash', 'true');

        const response = await api.get(`/api/files?${params.toString()}`);
        return response.data.files;
    },

    // Start chunked upload
    startUpload: async (file, options = {}) => {
        const response = await api.post('/upload/start', {
            filename: file.name,
            contentType: file.type,
            totalChunks: 1, // For now, single chunk
            sizeBytes: file.size,
            isPublic: options.isPublic ?? true,
            allowDownload: options.allowDownload ?? true,
            allowDelete: options.allowDelete ?? false,
            expiresIn: options.expiresIn || null,
        });
        return response.data;
    },

    // Upload file content as single chunk
    uploadChunk: async (fileId, file, chunkIndex = 0) => {
        const response = await api.put(`/upload/${fileId}/chunk/${chunkIndex}`, file, {
            headers: {
                'Content-Type': 'application/octet-stream',
            },
        });
        return response.data;
    },

    // Complete upload
    completeUpload: async (fileId) => {
        const response = await api.post(`/upload/${fileId}/complete`);
        return response.data;
    },

    // Upload file (convenience method combining start, chunk, complete)
    upload: async (file, options = {}) => {
        // Start upload
        const { id } = await filesApi.startUpload(file, options);

        // Upload single chunk
        await filesApi.uploadChunk(id, file, 0);

        // Complete upload
        await filesApi.completeUpload(id);

        return { id, filename: file.name };
    },

    // Download file
    download: async (fileId, token = null) => {
        const params = token ? `?t=${token}` : '';
        const response = await api.get(`/files/${fileId}${params}`, {
            responseType: 'blob',
        });
        return response.data;
    },

    // Preview file inline (primarily for public images)
    preview: async (fileId) => {
        const response = await api.get(`/files/${fileId}?preview=1`, {
            responseType: 'blob',
        });
        return response.data;
    },

    // Build a direct preview URL (useful for <img src=...>)
    getPreviewUrl: (fileId) => `${API_BASE_URL}/files/${fileId}?preview=1`,

    // Get file metadata
    getMetadata: async (fileId) => {
        const response = await api.get(`/files/${fileId}/metadata`);
        return response.data.file;
    },

    // Delete file
    delete: async (fileId) => {
        const response = await api.delete(`/files/${fileId}`);
        return response.data;
    },

    // Restore file
    restore: async (fileId) => {
        const response = await api.post(`/files/${fileId}/restore`);
        return response.data;
    },

    // Generate share token
    createToken: async (fileId, options = {}) => {
        const response = await api.post(`/files/${fileId}/token`, {
            expiresIn: options.expiresIn || 3600,
            maxUses: options.maxUses || 1,
        });
        return response.data;
    },

    // Update file visibility
    updateVisibility: async (fileId, isPublic) => {
        const response = await api.patch(`/files/${fileId}/visibility`, { isPublic });
        return response.data;
    },

    // Move file to directory
    move: async (fileId, directoryId) => {
        const response = await api.patch(`/files/${fileId}`, { directoryId });
        return response.data;
    },

    // Request access to a private file
    requestAccess: async (fileId) => {
        const response = await api.post(`/api/files/${fileId}/request-access`);
        return response.data;
    },

    // Get access requests for a file (owner/admin)
    getAccessRequests: async (fileId) => {
        const response = await api.get(`/api/files/${fileId}/access-requests`);
        return response.data.requests;
    },

    // Approve/Deny access request
    processAccessRequest: async (requestId, action) => {
        const response = await api.post(`/api/files/access-requests/${requestId}/${action}`);
        return response.data;
    },
};

// Directories API
export const directoriesApi = {
    // List all directories
    list: async () => {
        const response = await api.get('/api/directories');
        return response.data.directories;
    },

    // Create directory
    create: async (name) => {
        const response = await api.post('/api/directories', { name });
        return response.data;
    },

    // Delete directory
    delete: async (id) => {
        const response = await api.delete(`/api/directories/${id}`);
        return response.data;
    },
};

// Admin API
export const adminApi = {
    // Get pending users
    getPendingUsers: async () => {
        const response = await api.get('/api/admin/users/pending');
        return response.data.users;
    },

    // Approve user (with optional permissions)
    approveUser: async (userId, permissions = {}) => {
        const response = await api.post(`/api/admin/users/${userId}/approve`, permissions);
        return response.data;
    },

    // Reject user
    rejectUser: async (userId, reason) => {
        const response = await api.post(`/api/admin/users/${userId}/reject`, { reason });
        return response.data;
    },

    // Delete user
    deleteUser: async (userId) => {
        const response = await api.delete(`/api/admin/users/${userId}`);
        return response.data;
    },

    // Get security logs
    getLogs: async () => {
        const response = await api.get('/api/admin/logs');
        return response.data.logs;
    },

    // Get all users (for management)
    getAllUsers: async () => {
        const response = await api.get('/api/admin/users');
        return response.data.users;
    },

    // Update user permissions
    updatePermissions: async (userId, permissions) => {
        const response = await api.patch(`/api/admin/users/${userId}/permissions`, permissions);
        return response.data;
    },
};

// Auth API
export const authApi = {
    // Get current user info
    getMe: async () => {
        try {
            const response = await api.get('/auth/me');
            return response.data;
        } catch (error) {
            return { authenticated: false };
        }
    },

    // Login (redirects)
    login: () => {
        window.location.href = `${API_BASE_URL}/auth/login`;
    },

    // Logout
    logout: async () => {
        window.location.href = `${API_BASE_URL}/auth/logout`;
    },

    // Set session cookie using bridge token
    setSession: async (token) => {
        const response = await api.post('/auth/session', { token });
        return response.data;
    },

    // Request/Re-request access
    requestAccess: async () => {
        const response = await api.post('/api/access/request');
        return response.data;
    }
};

export default api;
